import { ExternalLink } from "lucide-react";
import { useProducts } from "../../lib/products";

/** `http`/`https` listing URLs only, so a stored string can't become a
 *  `javascript:` navigation. */
export function safeListingUrl(raw: string | undefined): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.toString();
}

/**
 * Listing link and captured price for a piece placed from a URL import.
 * Renders nothing for catalog pieces. Price prefers the snapshot on the
 * piece, then the products library (pieces placed before the snapshot).
 */
export function ListingProvenance({
  sourceUrl,
  sourcePrice,
  productId,
}: {
  sourceUrl?: string;
  sourcePrice?: string;
  productId?: string;
}) {
  const url = safeListingUrl(sourceUrl);
  const libraryPrice = useProducts((s) => {
    if (!productId) return undefined;
    return s.products.find((p) => p.id === productId)?.metadata?.price;
  });
  if (!url) return null;

  const price = sourcePrice?.trim() || libraryPrice?.trim() || undefined;
  const host = new URL(url).hostname;

  return (
    <div className="px-4 pt-1 pb-3" data-testid="listing-provenance">
      {price && (
        <>
          <div className="text-label uppercase tracking-wide font-semibold text-ink-500 mb-1">
            Price
          </div>
          <div
            className="text-sm font-semibold text-ink-900 tabular-nums"
            data-testid="listing-price"
          >
            {price}
          </div>
        </>
      )}
      <div
        className={
          price
            ? "mt-1.5 flex items-center justify-between gap-3"
            : "flex items-center justify-between gap-3"
        }
      >
        <span className="min-w-0 truncate text-caption text-ink-400">{host}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="listing-link"
          className="inline-flex shrink-0 items-center gap-1 text-caption font-medium text-ink-700 underline underline-offset-2 decoration-ink-300 hover:text-ink-900 hover:decoration-ink-900"
        >
          View listing
          <ExternalLink className="size-3" aria-hidden />
        </a>
      </div>
    </div>
  );
}
